import { motion, useReducedMotion } from "framer-motion";
import { ReactNode } from "react";
import {
    HeartPulse,
    Home,
    LucideIcon,
    Shield,
    Stethoscope,
    UserRound,
} from "lucide-react";
import { getLocalized, HealthLang, LocalizedString } from "./healthTypes";
import { HealthCoreMark } from "./HealthVisualMarks";
import { HealthHeroLoopAmbient } from "./HealthHeroLoopAmbient";
import { HERO_LOOP_CONNECTOR_POINTS } from "./healthLifecycleConstants";

/**
 * Orbit anchors — lg+ matches reference; below lg nudged slightly outward for breathing room.
 */
const HERO_LOOP_NODE_POSITION_CLASSES = [
    "top-[1%] left-1/2 -translate-x-1/2 lg:top-[4%]",
    "top-[20%] right-0 lg:top-[32%] lg:right-[4%]",
    "bottom-[5%] right-[5%] lg:bottom-[8%] lg:right-[18%]",
    "bottom-[5%] left-[5%] lg:bottom-[8%] lg:left-[18%]",
    "top-[20%] left-0 lg:top-[32%] lg:left-[4%]",
] as const;

const HERO_STAGE_ICONS: Record<string, LucideIcon> = {
    "home-health": Home,
    "preventive-care": Shield,
    "clinical-diagnosis": Stethoscope,
    "personalized-treatment": UserRound,
    "continuous-care": HeartPulse,
};

const STAGE_LABELS: {
    id: string;
    label: string;
    title: LocalizedString;
}[] = [
    {
        id: "home-health",
        label: "01",
        title: { En: "Home Health", Zh: "居家健康", Jp: "在宅ケア" },
    },
    {
        id: "preventive-care",
        label: "02",
        title: { En: "Preemptive Consultation", Zh: "預防諮詢", Jp: "予防相談" },
    },
    {
        id: "clinical-diagnosis",
        label: "03",
        title: { En: "Clinical Diagnosis", Zh: "臨床診斷", Jp: "臨床診断" },
    },
    {
        id: "personalized-treatment",
        label: "04",
        title: { En: "Personalized Treatment", Zh: "個人化治療", Jp: "個別化治療" },
    },
    {
        id: "continuous-care",
        label: "05",
        title: { En: "Smart Care", Zh: "智慧照護", Jp: "スマートケア" },
    },
];

const heroOrbitCardSurface =
    "flex w-[6.75rem] max-w-[42%] flex-col items-center rounded-2xl border-0 bg-white px-2 py-2 text-center shadow-md sm:w-[7.25rem] sm:max-w-[9rem] sm:rounded-3xl sm:px-2.5 sm:py-2.5 lg:w-[8rem] lg:max-w-[10rem] lg:px-3 lg:py-2.5";

const heroIconCircle =
    "mx-auto flex aspect-square h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-teal-300 bg-white p-0 leading-none lg:h-9 lg:w-9";

const HERO_ORBIT_ENTRANCE_EASE = [0.22, 1, 0.36, 1] as const;
const HERO_ORBIT_ENTRANCE_DURATION = 0.8;
const HERO_ORBIT_ENTRANCE_STAGGER = 0.1;
const HERO_ORBIT_ENTRANCE_BASE_DELAY = 0.12;

interface HealthLoopDiagramProps {
    centerLabel?: LocalizedString;
    centerSubLabel?: LocalizedString;
    lang: HealthLang;
    className?: string;
    variant?: "hero" | "platform" | "compact";
    showNodeTitles?: boolean;
}

function HeroOrbitCard({
    node,
    lang,
    positionClass,
    showNodeTitles,
    stageIndex,
    animateEntrance,
}: {
    node: (typeof STAGE_LABELS)[number];
    lang: HealthLang;
    positionClass: string;
    showNodeTitles: boolean;
    stageIndex: number;
    animateEntrance: boolean;
}) {
    const Icon = HERO_STAGE_ICONS[node.id] ?? Home;
    const reduceMotion = useReducedMotion();
    const shouldAnimate = animateEntrance && !reduceMotion;
    const entranceDelay =
        HERO_ORBIT_ENTRANCE_BASE_DELAY + stageIndex * HERO_ORBIT_ENTRANCE_STAGGER;

    const card = (
        <>
            <span className={heroIconCircle} aria-hidden="true">
                <Icon
                    className="block size-4 shrink-0 text-teal-500 lg:size-[1.125rem]"
                    strokeWidth={1.75}
                    aria-hidden="true"
                />
            </span>
            <p className="mt-1 w-full text-center text-[10px] font-semibold uppercase leading-none tracking-wide text-teal-500">
                Stage {node.label}
            </p>
            {showNodeTitles && (
                <p className="mt-0.5 flex min-h-[2.125rem] w-full items-center justify-center text-center text-[10px] font-semibold leading-tight text-grey-800 sm:min-h-[2.25rem] sm:text-[11px] lg:min-h-[2.5rem] lg:text-xs">
                    {getLocalized(node.title, lang)}
                </p>
            )}
        </>
    );

    const positionedWrap = (children: ReactNode) => (
        <div className={`absolute z-20 ${positionClass}`}>{children}</div>
    );

    if (!shouldAnimate) {
        return positionedWrap(
            <div className={heroOrbitCardSurface}>{card}</div>
        );
    }

    // Outer div keeps Tailwind position transforms (-translate-x-1/2, etc.);
    // inner motion only animates opacity + y so it cannot override placement.
    return positionedWrap(
        <motion.div
            className={heroOrbitCardSurface}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: HERO_ORBIT_ENTRANCE_DURATION,
                delay: entranceDelay,
                ease: HERO_ORBIT_ENTRANCE_EASE,
            }}
        >
            {card}
        </motion.div>
    );
}

const PLATFORM_POSITIONS = [
    "top-[2%] left-1/2 -translate-x-1/2",
    "top-[30%] right-[2%]",
    "bottom-[6%] right-[14%]",
    "bottom-[6%] left-[14%]",
    "top-[30%] left-[2%]",
];

/** Hero orbit — square aspect container so circles never stretch to ovals */
export function HealthLoopDiagram({
    centerLabel,
    centerSubLabel,
    lang,
    className = "",
    variant = "hero",
    showNodeTitles = true,
}: HealthLoopDiagramProps) {
    if (variant === "hero") {
        const heroHubLabel = getLocalized(centerLabel, lang) || "Aimma";
        const heroHubSubLabel = getLocalized(centerSubLabel, lang);
        return (
            <div
                className={`relative mx-auto aspect-square w-full max-w-[min(100%,360px)] shrink-0 min-[480px]:max-w-[min(100%,400px)] lg:max-w-[min(100%,520px)] ${className}`}
                aria-hidden
            >
                <svg
                    className="pointer-events-none absolute inset-0 z-0 h-full w-full"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="xMidYMid meet"
                >
                    {HERO_LOOP_CONNECTOR_POINTS.map((point, i) => (
                        <line
                            key={STAGE_LABELS[i].id}
                            x1="50"
                            y1="50"
                            x2={point.x}
                            y2={point.y}
                            stroke="rgba(3,124,132,0.18)"
                            strokeWidth="0.4"
                        />
                    ))}
                </svg>

                <HealthHeroLoopAmbient />

                {/* Dotted orbit — very slow rotation on desktop only */}
                <div
                    className="pointer-events-none absolute inset-[8%] z-[1] sm:inset-[10%] lg:inset-[12%]"
                    aria-hidden
                >
                    <div className="health-hero-orbit-ring-spin absolute inset-0 rounded-full border border-dashed border-[#57C4CB]/35" />
                </div>
                <div
                    className="pointer-events-none absolute inset-[20%] z-[2] rounded-full bg-white/70 shadow-[0_90px_300px_rgb(3_124_132/50%)] backdrop-blur-xl sm:inset-[22%] lg:inset-[23%]"
                    aria-hidden
                />
                <div className="absolute left-1/2 top-1/2 z-10 flex aspect-square w-[33%] min-w-[5.75rem] max-w-[13rem] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-gradient-to-br from-[#037C84] to-[#57C4CB] p-3 text-center text-white shadow-2xl shadow-cyan-200 sm:min-w-[6.5rem] sm:p-4 lg:min-w-[8rem] lg:p-5">
                    <span
                        className="text-lg leading-none sm:text-xl"
                        aria-hidden="true"
                    >
                        ✦
                    </span>
                    <span className="mt-1 max-w-[5.25rem] text-[11px] font-bold uppercase tracking-[0.16em] leading-tight sm:mt-1.5 sm:text-sm lg:mt-2 lg:max-w-[7rem] lg:text-base">
                        {heroHubLabel}
                    </span>
                    {heroHubSubLabel && (
                        <span className="mt-1 max-w-[5.5rem] text-[9px] font-medium leading-tight text-white/80 sm:max-w-[6rem] sm:text-[10px] lg:mt-1.5 lg:max-w-[7.5rem] lg:text-[11px]">
                            {heroHubSubLabel}
                        </span>
                    )}
                </div>

                {STAGE_LABELS.map((node, index) => (
                    <HeroOrbitCard
                        key={node.id}
                        node={node}
                        lang={lang}
                        positionClass={HERO_LOOP_NODE_POSITION_CLASSES[index]}
                        showNodeTitles={showNodeTitles}
                        stageIndex={index}
                        animateEntrance
                    />
                ))}
            </div>
        );
    }

    const hub = getLocalized(centerLabel, lang) || "Graphen Health AI Core";
    return (
        <PlatformLoopDiagram
            centerLabel={hub}
            lang={lang}
            className={className}
            variant={variant}
        />
    );
}

function PlatformLoopDiagram({
    centerLabel,
    lang,
    className,
    variant,
}: {
    centerLabel: string;
    lang: HealthLang;
    className: string;
    variant: "platform" | "compact";
}) {
    const maxSize =
        variant === "platform"
            ? "max-w-[min(100%,320px)]"
            : "max-w-[min(100%,260px)]";
    const nodeWidth = variant === "platform" ? "max-w-[6.5rem]" : "max-w-[5.5rem]";
    const hubFraction = variant === "platform" ? "w-[32%]" : "w-[36%]";

    return (
        <div
            className={`relative mx-auto aspect-square w-full shrink-0 ${maxSize} ${className}`}
            aria-hidden
        >
            <svg
                className="pointer-events-none absolute inset-0 h-full w-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="xMidYMid meet"
            >
                <circle
                    cx="50"
                    cy="50"
                    r="42"
                    fill="none"
                    stroke="rgba(87,196,203,0.55)"
                    strokeWidth="0.4"
                    strokeDasharray="1.5 1.5"
                />
            </svg>
            <div
                className={`absolute left-1/2 top-1/2 z-10 flex ${hubFraction} aspect-square -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border-2 border-teal-400 bg-teal-500 p-2 text-center text-white`}
            >
                <HealthCoreMark className="h-[30%] w-[30%] min-h-5 min-w-5" stroke="white" />
                <span className="mt-1 px-1 text-center text-[10px] font-semibold leading-tight">
                    {centerLabel}
                </span>
            </div>
            {STAGE_LABELS.map((node, index) => (
                <div
                    key={node.label}
                    className={`absolute z-20 flex min-w-0 flex-col items-center ${PLATFORM_POSITIONS[index]} ${nodeWidth}`}
                >
                    <span className="flex aspect-square h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-teal-400 bg-white text-xs font-bold text-teal-500">
                        {node.label}
                    </span>
                    <p className="mt-1.5 w-full text-center text-[11px] font-semibold leading-snug text-grey-700 [text-align:center]">
                        {getLocalized(node.title, lang)}
                    </p>
                </div>
            ))}
        </div>
    );
}
